import { useAuth } from "../context/AuthContext";
import {
  // Basic permissions
  CAN_AGENT,
  CAN_PUBLIC,
  CAN_EDIT_SYSTEM_SETTINGS,
  CAN_VIEW_REPORTS,
  CAN_SPECIAL_EDIT,

  // Vehicle Management
  CAN_ADD_VEHICLE,

  // Booking Management
  CAN_INITIATE_RESERVATION,
  CAN_APPROVE_RESERVATION,
  CAN_REJECT_RESERVATION,
  CAN_VIEW_SUBMITTED_BOOKINGS,
  CAN_VIEW_APPROVED_BOOKINGS,
  CAN_VIEW_REJECTED_BOOKINGS,
  CAN_VIEW_ADVANCED_BOOKINGS,
  CAN_VIEW_STICKER_BOOKINGS,
  CAN_MAKE_AN_ADVANCE_PAYMENT,

  // Parking Management
  CAN_ASSIGN_REASSIGN_PARKING_YARDS,
  CAN_ADD_VIEW_EDIT_DELETE_PARKING_YARDS,
  CAN_ADD_VIEW_EDIT_DELETE_PARKING_SLOTS,
  CAN_ADD_VIEW_EDIT_DELETE_PARKING_RATES,
  CAN_ADD_VIEW_EDIT_DELETE_FEES_PAYMENT_INTERVAL,
  CAN_ADD_VIEW_EDIT_DELETE_FEES_AMOUNT,
  CAN_ADD_VIEW_EDIT_DELETE_VEHICLE_SIZE,
  CAN_ADD_VIEW_EDIT_DELETE_RESERVATION_RATES,
  CAN_VIEW_PARKED_CARS,
  CAN_MANAGE_PARKING,
  CAN_SEARCH_FOR_PARKING_SLOT,
  CAN_NAVIGATE_TO_PARKING_SLOT,
  CAN_VIEW_PARKING_LIST,

  // User Management
  CAN_ASSIGN_PERMISSIONS,
  CAN_EDIT_ROLES,
  CAN_ADD_USERS,
  CAN_VIEW_USERS,
  CAN_DEACTIVATE_USERS,
  CAN_REACTIVATE_USERS,
  CAN_ASSIGN_USER_ROLES,
  CAN_EDIT_USER_PROFILE,

  // Agent and Staff Management
  CAN_ADD_AGENTS,
  CAN_ADD_STAFF,
  CAN_VIEW_AGENTS,
  CAN_VIEW_STAFF,
  CAN_EDIT_AGENT_DETAILS,
  CAN_EDIT_STAFF_DETAILS,
  CAN_DEACTIVATE_AGENTS_AND_STAFF,
  CAN_VIEW_AGENT_LOGS,
  CAN_VIEW_AGENT_ACCOUNT_BALANCE,

  // System Settings
  CAN_ADD_SYSTEM_SETTINGS,
  CAN_ADD_VIEW_EDIT_DELETE_CHART_OF_ACCOUNTS,
  CAN_ADD_VIEW_EDIT_DELETE_PAYMENT_MODES,
  CAN_ADD_VIEW_EDIT_DELETE_TELECOM_CODES,
  CAN_ADD_VIEW_EDIT_DELETE_TELECOM_CHARGES,
  CAN_ADD_VIEW_EDIT_DELETE_WORKFLOWS,
  CAN_ADD_VIEW_EDIT_DELETE_MASTER_DATA,

  // Reporting
  CAN_VIEW_SYSTEM_LOGS,
  CAN_VIEW_AUDIT_TRAIL,
  CAN_VIEW_MONITORING_REPORTS,
  CAN_VIEW_CHART_OF_ACCOUNTS_REPORTS,
  CAN_VIEW_LEDGER_REPORTS,
  CAN_VIEW_REVENUE_PERFORMANCE_REPORTS,
  CAN_VIEW_PARKING_SLOT_OCCUPANCY_REPORTS,
  CAN_VIEW_DEFAULTERS_REPORTS,
  CAN_VIEW_PAYMENTS_REPORTS,
  CAN_VIEW_GENERAL_LEDGER,
  CAN_VIEW_PAYMENTS_REGISTER,
  CAN_VIEW_INVOICES,
  CAN_VIEW_PAYMENT_REQUESTS,
  CAN_VIEW_DASHBOARD,
  CAN_VIEW_COUPON_SALES_REPORTS,
  CAN_RECONCILE,
  CAN_ENFORCE,
  CAN_APPROVE_LIQUIDATION_REQUEST,
} from "./constants";

// Permission arrays for each role using constants
const ADMIN_PERMISSIONS = [
  CAN_ASSIGN_PERMISSIONS,
  CAN_EDIT_ROLES,
  CAN_ADD_USERS,
  CAN_VIEW_USERS,
  CAN_DEACTIVATE_USERS,
  CAN_REACTIVATE_USERS,
  CAN_ASSIGN_USER_ROLES,
  CAN_EDIT_USER_PROFILE,
  CAN_SPECIAL_EDIT,
  CAN_ADD_AGENTS,
  CAN_ADD_STAFF,
  CAN_VIEW_AGENTS,
  CAN_VIEW_STAFF,
  CAN_EDIT_AGENT_DETAILS,
  CAN_EDIT_STAFF_DETAILS,
  CAN_DEACTIVATE_AGENTS_AND_STAFF,
  CAN_VIEW_AGENT_LOGS,
  CAN_VIEW_AGENT_ACCOUNT_BALANCE,
  CAN_ADD_VIEW_EDIT_DELETE_CHART_OF_ACCOUNTS,
  CAN_ADD_VIEW_EDIT_DELETE_WORKFLOWS,
  CAN_ADD_VIEW_EDIT_DELETE_MASTER_DATA,
  CAN_ASSIGN_REASSIGN_PARKING_YARDS,
  CAN_VIEW_PARKED_CARS,
  CAN_VIEW_SYSTEM_LOGS,
  CAN_VIEW_AUDIT_TRAIL,
  CAN_VIEW_MONITORING_REPORTS,
  CAN_VIEW_CHART_OF_ACCOUNTS_REPORTS,
  CAN_VIEW_LEDGER_REPORTS,
  CAN_VIEW_REVENUE_PERFORMANCE_REPORTS,
  CAN_VIEW_PARKING_SLOT_OCCUPANCY_REPORTS,
  CAN_VIEW_DEFAULTERS_REPORTS,
  CAN_VIEW_PAYMENTS_REPORTS,
  CAN_VIEW_GENERAL_LEDGER,
  CAN_VIEW_PAYMENTS_REGISTER,
  CAN_VIEW_INVOICES,
  CAN_VIEW_PAYMENT_REQUESTS,
  CAN_VIEW_DASHBOARD,
  CAN_VIEW_SUBMITTED_BOOKINGS,
  CAN_VIEW_APPROVED_BOOKINGS,
  CAN_VIEW_REJECTED_BOOKINGS,
  CAN_VIEW_ADVANCED_BOOKINGS,
  CAN_VIEW_STICKER_BOOKINGS,
];

const SUPER_ADMIN_PERMISSIONS = [
  CAN_ADD_AGENTS,
  CAN_ADD_STAFF,
  CAN_ADD_SYSTEM_SETTINGS,
  CAN_ADD_USERS,
  CAN_ADD_VIEW_EDIT_DELETE_CHART_OF_ACCOUNTS,
  CAN_ADD_VIEW_EDIT_DELETE_FEES_AMOUNT,
  CAN_ADD_VIEW_EDIT_DELETE_FEES_PAYMENT_INTERVAL,
  CAN_ADD_VIEW_EDIT_DELETE_MASTER_DATA,
  CAN_ADD_VIEW_EDIT_DELETE_PARKING_RATES,
  CAN_ADD_VIEW_EDIT_DELETE_PARKING_SLOTS,
  CAN_ADD_VIEW_EDIT_DELETE_PARKING_YARDS,
  CAN_ADD_VIEW_EDIT_DELETE_PAYMENT_MODES,
  CAN_ADD_VIEW_EDIT_DELETE_RESERVATION_RATES,
  CAN_ADD_VIEW_EDIT_DELETE_TELECOM_CHARGES,
  CAN_ADD_VIEW_EDIT_DELETE_TELECOM_CODES,
  CAN_ADD_VIEW_EDIT_DELETE_VEHICLE_SIZE,
  CAN_ADD_VIEW_EDIT_DELETE_WORKFLOWS,
  CAN_ASSIGN_PERMISSIONS,
  CAN_ASSIGN_REASSIGN_PARKING_YARDS,
  CAN_ASSIGN_USER_ROLES,
  CAN_DEACTIVATE_AGENTS_AND_STAFF,
  CAN_DEACTIVATE_USERS,
  CAN_EDIT_AGENT_DETAILS,
  CAN_EDIT_ROLES,
  CAN_EDIT_STAFF_DETAILS,
  CAN_EDIT_SYSTEM_SETTINGS,
  CAN_EDIT_USER_PROFILE,
  CAN_REACTIVATE_USERS,
  CAN_SPECIAL_EDIT,
  CAN_VIEW_ADVANCED_BOOKINGS,
  CAN_VIEW_AGENT_ACCOUNT_BALANCE,
  CAN_VIEW_AGENT_LOGS,
  CAN_VIEW_AGENTS,
  CAN_VIEW_APPROVED_BOOKINGS,
  CAN_VIEW_AUDIT_TRAIL,
  CAN_VIEW_CHART_OF_ACCOUNTS_REPORTS,
  CAN_VIEW_DASHBOARD,
  CAN_VIEW_DEFAULTERS_REPORTS,
  CAN_VIEW_GENERAL_LEDGER,
  CAN_VIEW_INVOICES,
  CAN_VIEW_LEDGER_REPORTS,
  CAN_VIEW_MONITORING_REPORTS,
  CAN_VIEW_PARKED_CARS,
  CAN_VIEW_PARKING_SLOT_OCCUPANCY_REPORTS,
  CAN_VIEW_PAYMENT_REQUESTS,
  CAN_VIEW_PAYMENTS_REGISTER,
  CAN_VIEW_PAYMENTS_REPORTS,
  CAN_VIEW_REJECTED_BOOKINGS,
  CAN_VIEW_REVENUE_PERFORMANCE_REPORTS,
  CAN_VIEW_STAFF,
  CAN_VIEW_STICKER_BOOKINGS,
  CAN_VIEW_SUBMITTED_BOOKINGS,
  CAN_VIEW_SYSTEM_LOGS,
  CAN_VIEW_USERS,
];

const ACCOUNTANT_PERMISSIONS = [
  CAN_VIEW_ADVANCED_BOOKINGS,
  CAN_VIEW_AGENT_ACCOUNT_BALANCE,
  CAN_VIEW_AGENTS,
  CAN_VIEW_APPROVED_BOOKINGS,
  CAN_VIEW_CHART_OF_ACCOUNTS_REPORTS,
  CAN_VIEW_DASHBOARD,
  CAN_VIEW_DEFAULTERS_REPORTS,
  CAN_VIEW_GENERAL_LEDGER,
  CAN_VIEW_INVOICES,
  CAN_VIEW_LEDGER_REPORTS,
  CAN_VIEW_PARKED_CARS,
  CAN_VIEW_PARKING_SLOT_OCCUPANCY_REPORTS,
  CAN_VIEW_PAYMENT_REQUESTS,
  CAN_VIEW_PAYMENTS_REGISTER,
  CAN_VIEW_PAYMENTS_REPORTS,
  CAN_VIEW_REJECTED_BOOKINGS,
  CAN_VIEW_REVENUE_PERFORMANCE_REPORTS,
  CAN_VIEW_STAFF,
  CAN_VIEW_STICKER_BOOKINGS,
  CAN_VIEW_SUBMITTED_BOOKINGS,
];

const HEAD_OF_ACCOUNTANT_PERMISSIONS = [
  CAN_VIEW_AGENTS,
  CAN_VIEW_STAFF,
  CAN_VIEW_AGENT_ACCOUNT_BALANCE,
  CAN_VIEW_PARKED_CARS,
  CAN_VIEW_CHART_OF_ACCOUNTS_REPORTS,
  CAN_VIEW_LEDGER_REPORTS,
  CAN_VIEW_REVENUE_PERFORMANCE_REPORTS,
  CAN_VIEW_PARKING_SLOT_OCCUPANCY_REPORTS,
  CAN_VIEW_DEFAULTERS_REPORTS,
  CAN_VIEW_PAYMENTS_REPORTS,
  CAN_VIEW_GENERAL_LEDGER,
  CAN_VIEW_PAYMENTS_REGISTER,
  CAN_VIEW_INVOICES,
  CAN_VIEW_PAYMENT_REQUESTS,
  CAN_VIEW_DASHBOARD,
  CAN_VIEW_COUPON_SALES_REPORTS,
  CAN_VIEW_SUBMITTED_BOOKINGS,
  CAN_VIEW_APPROVED_BOOKINGS,
  CAN_VIEW_REJECTED_BOOKINGS,
  CAN_VIEW_ADVANCED_BOOKINGS,
  CAN_VIEW_STICKER_BOOKINGS,
  CAN_RECONCILE,
];

const LIQUIDATION_REQUEST_APPROVER_PERMISSIONS = [
  CAN_VIEW_AGENTS,
  CAN_VIEW_STAFF,
  CAN_VIEW_AGENT_ACCOUNT_BALANCE,
  CAN_VIEW_PARKED_CARS,
  CAN_VIEW_CHART_OF_ACCOUNTS_REPORTS,
  CAN_VIEW_LEDGER_REPORTS,
  CAN_VIEW_REVENUE_PERFORMANCE_REPORTS,
  CAN_VIEW_PARKING_SLOT_OCCUPANCY_REPORTS,
  CAN_VIEW_DEFAULTERS_REPORTS,
  CAN_VIEW_PAYMENTS_REPORTS,
  CAN_VIEW_GENERAL_LEDGER,
  CAN_VIEW_PAYMENTS_REGISTER,
  CAN_VIEW_INVOICES,
  CAN_VIEW_PAYMENT_REQUESTS,
  CAN_VIEW_DASHBOARD,
  CAN_VIEW_COUPON_SALES_REPORTS,
  CAN_VIEW_SUBMITTED_BOOKINGS,
  CAN_VIEW_APPROVED_BOOKINGS,
  CAN_VIEW_REJECTED_BOOKINGS,
  CAN_VIEW_ADVANCED_BOOKINGS,
  CAN_VIEW_STICKER_BOOKINGS,
  CAN_RECONCILE,
  CAN_APPROVE_LIQUIDATION_REQUEST,
];

const MANAGER_PERMISSIONS = [
  CAN_APPROVE_RESERVATION,
  CAN_INITIATE_RESERVATION,
  CAN_REJECT_RESERVATION,
  CAN_VIEW_ADVANCED_BOOKINGS,
  CAN_VIEW_AGENTS,
  CAN_VIEW_APPROVED_BOOKINGS,
  CAN_VIEW_DASHBOARD,
  CAN_VIEW_DEFAULTERS_REPORTS,
  CAN_VIEW_PARKED_CARS,
  CAN_VIEW_PARKING_SLOT_OCCUPANCY_REPORTS,
  CAN_VIEW_REJECTED_BOOKINGS,
  CAN_VIEW_STAFF,
  CAN_VIEW_STICKER_BOOKINGS,
  CAN_VIEW_SUBMITTED_BOOKINGS,
];

const MIS_AUTHORITY_PERMISSIONS = [
  CAN_VIEW_CHART_OF_ACCOUNTS_REPORTS,
  CAN_VIEW_DASHBOARD,
  CAN_VIEW_DEFAULTERS_REPORTS,
  CAN_VIEW_PARKING_SLOT_OCCUPANCY_REPORTS,
  CAN_VIEW_REVENUE_PERFORMANCE_REPORTS,
];

const ENFORCEMENT_PERMISSIONS = [
  CAN_VIEW_DASHBOARD,
  CAN_VIEW_DEFAULTERS_REPORTS,
  CAN_VIEW_PARKED_CARS,
  CAN_ENFORCE,
  CAN_VIEW_PARKING_SLOT_OCCUPANCY_REPORTS,
];

const STAFF_PERMISSIONS = [CAN_MANAGE_PARKING, CAN_VIEW_REPORTS];

// Public user permissions based on your constants
const PUBLIC_USER_PERMISSIONS = [
  CAN_ADD_VEHICLE,
  CAN_INITIATE_RESERVATION,
  CAN_MAKE_AN_ADVANCE_PAYMENT,
  CAN_NAVIGATE_TO_PARKING_SLOT,
  CAN_SEARCH_FOR_PARKING_SLOT,
  CAN_VIEW_ADVANCED_BOOKINGS,
  CAN_VIEW_APPROVED_BOOKINGS,
  CAN_VIEW_INVOICES,
  CAN_VIEW_PARKING_LIST,
  CAN_VIEW_PAYMENTS_REPORTS,
  CAN_VIEW_REJECTED_BOOKINGS,
  CAN_VIEW_STICKER_BOOKINGS,
  CAN_VIEW_SUBMITTED_BOOKINGS,
];

export const IsSuperUser = () => {
  const { user } = useAuth();
  return user?.is_superuser || false;
};

export const IsPublicUser = () => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  const userPermissions = user.role_details[0].permissions.map(
    (p) => p?.codename,
  );

  // Check if user has the CAN_PUBLIC permission
  const hasPublicPermission = userPermissions.includes(CAN_PUBLIC);

  // Alternative: Check if user's role name is "Public User"
  const isPublicRole = user?.role_details?.[0]?.name === "Public User";

  // Check if user has the core public user permissions
  const hasPublicUserPermissions = PUBLIC_USER_PERMISSIONS.every((permission) =>
    userPermissions.includes(permission),
  );

  // Return true if any condition is met
  return Boolean(
    hasPublicPermission && isPublicRole && hasPublicUserPermissions,
  );
};

export const ISAgentUser = () => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  // Check if user has the CAN_AGENT permission
  const hasAgentPermission = user.role_details[0].permissions.find(
    (v) => v?.codename === CAN_AGENT,
  );

  // Alternative: Check if user's role name is "Agent"
  const isAgentRole = user?.role_details?.[0]?.name === "Agent";

  return Boolean(hasAgentPermission && isAgentRole);
};

// Check if user is Super Admin
export const IsSuperAdmin = () => {
  const { user } = useAuth();

  // Check if user is marked as superuser
  if (user?.is_superuser) return true;

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  const userPermissions = user.role_details[0].permissions.map(
    (p) => p?.codename,
  );
  const roleName = user?.role_details?.[0]?.name;

  // Check by role name
  if (roleName === "Super Admin") return true;

  // Check if user has majority of super admin permissions (at least 80%)
  const hasRequiredPermissions =
    SUPER_ADMIN_PERMISSIONS.filter((permission) =>
      userPermissions.includes(permission),
    ).length >= Math.floor(SUPER_ADMIN_PERMISSIONS.length * 0.8);

  return hasRequiredPermissions;
};

//Check if user is  Admin
export const IsAdmin = () => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  const userPermissions = user.role_details[0].permissions.map(
    (p) => p?.codename,
  );

  const roleName = user?.role_details?.[0]?.name;

  // Check by role name
  if (roleName === "Admin") return true;

  const hasRequiredPermissions =
    ADMIN_PERMISSIONS.filter((permission) =>
      userPermissions.includes(permission),
    ).length >= Math.floor(ADMIN_PERMISSIONS.length * 0.8);

  return hasRequiredPermissions;
};

// Check if user is Accountant
export const IsAccountant = () => {
  const { user } = useAuth();
  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }
  const userPermissions = user.role_details[0].permissions.map((p) => {
    p?.codename;
  });
  const roleName = user?.role_details?.[0]?.name;

  // Check by role name
  if (roleName === "Accountant") return true;

  // Check if user has majority of accountant permissions (at least 70%)
  const hasRequiredPermissions =
    ACCOUNTANT_PERMISSIONS.filter((permission) =>
      userPermissions.includes(permission),
    ).length >= Math.floor(ACCOUNTANT_PERMISSIONS.length * 0.7);

  return hasRequiredPermissions;
};

// Check if user is Manager
export const IsManager = () => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  const userPermissions = user.role_details[0].permissions.map(
    (p) => p?.codename,
  );
  const roleName = user?.role_details?.[0]?.name;

  // Check by role name
  if (roleName === "Manager") return true;

  // Check if user has majority of manager permissions (at least 70%)
  const hasRequiredPermissions =
    MANAGER_PERMISSIONS.filter((permission) =>
      userPermissions.includes(permission),
    ).length >= Math.floor(MANAGER_PERMISSIONS.length * 0.7);

  return hasRequiredPermissions;
};

// Check if user is MIS Authority
export const IsMISAuthority = () => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  const userPermissions = user.role_details[0].permissions.map(
    (p) => p?.codename,
  );
  const roleName = user?.role_details?.[0]?.name;

  // Check by role name
  if (roleName === "MIS Authority") return true;

  // Check if user has all MIS Authority permissions
  const hasRequiredPermissions = MIS_AUTHORITY_PERMISSIONS.every((permission) =>
    userPermissions.includes(permission),
  );

  return hasRequiredPermissions;
};

// Check if user is Enforcement
export const IsEnforcement = () => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  const userPermissions = user.role_details[0].permissions.map(
    (p) => p?.codename,
  );
  const roleName = user?.role_details?.[0]?.name;

  // Check by role name
  if (roleName === "Enforcement") return true;

  // Check if user has all enforcement permissions
  const hasRequiredPermissions = ENFORCEMENT_PERMISSIONS.every((permission) =>
    userPermissions.includes(permission),
  );

  return hasRequiredPermissions;
};

export const IsHeadOfAccountant = () => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  const userPermissions = user.role_details[0].permissions.map(
    (p) => p?.codename,
  );
  const roleName = user?.role_details?.[0]?.name;

  // Check by role name
  if (roleName === "Head Accountant") return true;

  // Check if user has majority of head of accountant permissions (at least 70%)
  const hasRequiredPermissions =
    HEAD_OF_ACCOUNTANT_PERMISSIONS.filter((permission) =>
      userPermissions.includes(permission),
    ).length >= Math.floor(HEAD_OF_ACCOUNTANT_PERMISSIONS.length * 0.7);
  return hasRequiredPermissions;
};

export const IsLiquidationRequestApprover = () => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  const userPermissions = user.role_details[0].permissions.map(
    (p) => p?.codename,
  );
  const roleName = user?.role_details?.[0]?.name;

  // Check by role name
  if (roleName === "Liquidation Approver") return true;

  // Check if user has majority of liquidation request approver permissions (at least 70%)
  const hasRequiredPermissions =
    LIQUIDATION_REQUEST_APPROVER_PERMISSIONS.filter((permission) =>
      userPermissions.includes(permission),
    ).length >=
    Math.floor(LIQUIDATION_REQUEST_APPROVER_PERMISSIONS.length * 0.7);
  return hasRequiredPermissions;
};

// Check if user is Staff
export const IsStaff = () => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  const userPermissions = user.role_details[0].permissions.map(
    (p) => p?.codename,
  );
  const roleName = user?.role_details?.[0]?.name;

  // Check by role name
  if (roleName === "Staff") return true;

  // Check if user has staff permissions
  const hasRequiredPermissions = STAFF_PERMISSIONS.every((permission) =>
    userPermissions.includes(permission),
  );

  return hasRequiredPermissions;
};

export const isSelectedRoleStaff = (selectedGroups, rolesData) => {
  if (!selectedGroups || !rolesData) return false;

  // If groups is a single value (role ID)
  const selectedRole = rolesData.find((role) => role.id === selectedGroups);
  if (selectedRole?.name === "Staff") return true;

  // If groups is an array of role IDs
  if (Array.isArray(selectedGroups)) {
    return selectedGroups.some((groupId) => {
      const role = rolesData.find((role) => role.id === groupId);
      return role?.name === "Staff";
    });
  }

  return false;
};

export const isStaffSelected = (selectedGroupId, rolesData) => {
  if (!selectedGroupId || !rolesData) return false;

  const selectedRole = rolesData.find((role) => role.id === selectedGroupId);
  return selectedRole?.name === "Staff";
};

export const isAgentSelected = (selectedGroups, rolesData) => {
  if (!selectedGroups || !rolesData) return false;

  // Handle both single value and array cases
  const groupsArray = Array.isArray(selectedGroups)
    ? selectedGroups
    : [selectedGroups];

  // Check if any of the selected groups is "Agent"
  return groupsArray.some((groupId) => {
    const selectedRole = rolesData.find((role) => role.id === groupId);
    return selectedRole?.name === "Agent";
  });
};

// Additional helper function to check if user is Public User by role name only
export const IsPublicUserByRole = () => {
  const { user } = useAuth();
  return user?.role_details?.[0]?.name === "Public User";
};

// Additional helper function to check if user is Agent by role name only
export const IsAgentUserByRole = () => {
  const { user } = useAuth();
  return user?.role_details?.[0]?.name === "Agent";
};

// Helper function to get user's role name
export const GetUserRoleName = () => {
  const { user } = useAuth();
  return user?.role_details?.[0]?.name || null;
};

// Helper function to check if user has specific permission
export const HasPermission = (permissionCode) => {
  const { user } = useAuth();

  if (!user?.role_details?.[0]?.permissions) {
    return false;
  }

  return Boolean(
    user.role_details[0].permissions.find(
      (permission) => permission?.codename === permissionCode,
    ),
  );
};
